import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { type FC, useCallback, useContext, useRef } from 'react';

import { SlabContext } from '@core/context/SlabContext';
import type { ProductsByPlantFormikType } from '@core/formik/quote';
import type { Address } from '@core/types/api/Address/Address';
import Enums from '@core/types/api/Enums';
import { hasValue } from '@core/utils/equality';
import { withFirstProductRowPending } from '@core/utils/locationRows';
import {
  ButtonWithPopover,
  type ButtonWithPopoverHandleRef,
} from '@web/components/ButtonWithPopover/ButtonWithPopover';
import { PlantAccordionFormik } from '@web/components/PlantAccordionFormik/PlantAccordionFormik';
import {
  ProductSection,
  type ProductSectionProps,
} from '@web/components/ProductSection/ProductSection';
import { SquaredBadge } from '@web/components/SquaredBadge/SquaredBadge';
import { useGetPlantsOptionsWithDistances } from '@web/hooks/plantsDistance/useGetPlantsOptionsWithDistances';

import { CopyLocationForm } from '../../CopyLocationForm/CopyLocationForm';
import { useQuoteDrawerContext } from '../QuoteDrawer';
import type { QuoteDrawerFormikType } from '../utils';

interface LocationFormProps {
  index: number;
  sectionType: 'main' | 'additional';
  arrayHelpers: {
    remove: (index: number) => void;
  };
  curProductsRef: ProductSectionProps['curProductsRef'];
  project: ProductSectionProps['project'];
  projectProductsByProductID: ProductSectionProps['projectProductsByProductID'];
  onCopyProductsToLocations: (toLocations: ProductsByPlantFormikType[]) => void;
}

const LocationForm: FC<LocationFormProps> = ({
  index,
  sectionType,
  arrayHelpers,
  curProductsRef,
  project,
  projectProductsByProductID,
  onCopyProductsToLocations,
}) => {
  const { readOnly, isReconcilingBuyerPricing } = useQuoteDrawerContext();
  const { userInfo } = useContext(SlabContext);

  const isMultiLocationEnabled = userInfo.hasFlags([
    Enums.FeatureFlagName.FeatureFlagMultiLocationEnabled,
  ]);

  // When multi-location is disabled:
  // - Only the first location in main section can be edited
  // - Additional services location is synced with main, so it should be disabled
  const isLocationDisabled = !isMultiLocationEnabled && index > 0 && sectionType === 'additional';

  const formikBag = useFormikContext<QuoteDrawerFormikType>();
  const { quickAddress, quickProjectMode } = formikBag.values;
  const quickAddressFallback = quickProjectMode === 'new' ? quickAddress : undefined;
  const effectiveAddress: Partial<Address> = project?.address ?? quickAddressFallback ?? {};

  // Get the appropriate array based on section type
  const plants =
    sectionType === 'main'
      ? formikBag.values.mainProductsByPlants
      : formikBag.values.additionalProductsByPlants;

  // Get all selected plant IDs except the current one from the same section
  const currentSectionPlants =
    sectionType === 'main'
      ? formikBag.values.mainProductsByPlants
      : formikBag.values.additionalProductsByPlants;

  const otherSelectedPlantIds = currentSectionPlants
    .filter((_, idx) => idx !== index)
    .map((loc) => loc.plant?.id)
    .filter((id) => hasValue(id)) as string[];

  const allPlantOptions = useGetPlantsOptionsWithDistances({
    currentAddress: effectiveAddress,
  });

  const plant = plants[index];
  const hasSelectedPlant = hasValue(plant.plant.id);

  const quoteProductKind =
    sectionType === 'main' ? Enums.QuoteProductKind.Primary : Enums.QuoteProductKind.Additional;

  const fieldNamePrefix =
    sectionType === 'main' ? 'mainProductsByPlants' : 'additionalProductsByPlants';

  const plantId = plant.plant?.id;
  const onMatchChange = useCallback(
    (value: string | null) => {
      // check if location is changed and if it is, reset the products
      if (value !== null && value !== plantId) {
        const productsPath = `${fieldNamePrefix}.${index}.products`;
        formikBag.setFieldValue(productsPath, []);
        formikBag.setStatus(withFirstProductRowPending(formikBag.status, productsPath, value));
      }
      // Set distance and time if available
      const distanceInfo = allPlantOptions.distances.get(value ?? '');
      if (distanceInfo) {
        formikBag.setFieldValue(
          `${fieldNamePrefix}.${index}.plantDistance`,
          distanceInfo.plantDistance,
        );
        formikBag.setFieldValue(
          `${fieldNamePrefix}.${index}.plantDistanceUnit`,
          distanceInfo.plantDistanceUnit,
        );
        formikBag.setFieldValue(
          `${fieldNamePrefix}.${index}.plantDriveTime`,
          distanceInfo.plantDriveTime,
        );
      } else {
        formikBag.setFieldValue(`${fieldNamePrefix}.${index}.plantDistance`, null);
        formikBag.setFieldValue(`${fieldNamePrefix}.${index}.plantDistanceUnit`, null);
        formikBag.setFieldValue(`${fieldNamePrefix}.${index}.plantDriveTime`, null);
      }
    },
    [plantId, formikBag, fieldNamePrefix, index, allPlantOptions],
  );

  const copyPopoverRef = useRef<ButtonWithPopoverHandleRef | null>(null);
  const plantProducts = plant.products.filter((product) => !!product.product.option?.label);

  return (
    <PlantAccordionFormik
      formState={formikBag.values}
      projectAddress={effectiveAddress}
      index={index}
      formikName={`${fieldNamePrefix}.${index}.plant`}
      otherSelectedPlantIds={otherSelectedPlantIds}
      arrayHelpers={arrayHelpers}
      onMatchChange={onMatchChange}
      readOnly={readOnly}
      plantDisabled={!isMultiLocationEnabled && sectionType === 'additional'}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {hasSelectedPlant && (
          <>
            {plant.products.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant='body1' sx={{ fontWeight: 500 }}>
                    Selected Products:
                  </Typography>
                  <SquaredBadge badgeContent={plant.products.length} />
                </Box>
                <ButtonWithPopover
                  startIcon={<ContentCopyIcon />}
                  sx={{ marginLeft: 'auto' }}
                  title='Copy To...'
                  handle={copyPopoverRef}
                  disabled={readOnly || plantProducts.length === 0}
                >
                  <CopyLocationForm
                    plantName={plant.plant.option?.label || ''}
                    products={plantProducts}
                    plantDistanceInfos={allPlantOptions.distances}
                    onCopy={onCopyProductsToLocations}
                    onClose={() => copyPopoverRef.current?.close()}
                    excludePlantIds={currentSectionPlants.map((p) => p.plant.id || '')}
                    sectionType={quoteProductKind}
                    curProductsRef={curProductsRef}
                  />
                </ButtonWithPopover>
              </Box>
            )}

            <ProductSection
              readOnly={readOnly || isLocationDisabled}
              sectionType={quoteProductKind}
              plant={plant.plant}
              curProductsRef={curProductsRef}
              project={project}
              projectProductsByProductID={projectProductsByProductID}
              plantIndex={index}
              isPriceSettled={!isReconcilingBuyerPricing}
            />
          </>
        )}
      </Box>
    </PlantAccordionFormik>
  );
};

export default LocationForm;
